import React from 'react';
import Box from '@redbubble/design-system/react/Box';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';

import TextLink from '@redbubble/design-system/react/TextLink';
import ChevronRightIcon from '@redbubble/design-system/react/Icons/ChevronRight';
import * as constants from '@redbubble/design-system/react/constants';

import FavoritableItemCarousel, {
  fragments as carouselFragments
} from '../FavoritableItemCarousel';
import { fragments as favoriteFragments } from '../../containers/apollo/withFavoriteStatuses';
import CarouselHeading from '../CarouselHeading';
import { shopTagPath } from '../../lib/routing';
import { useStrikethroughFeatureFlag } from '../../hooks';
import { useTopicProductRecommendationsCollection } from '../../containers/apollo/withTopicProductRecommendations';

import {
  analyticsPayload,
  TOPIC_PRODUCT_RECOMMENDATIONS_SOURCE,
  TOPIC_PRODUCT_RECOMMENDATIONS_LABEL
} from '../../lib/analytics';
import { AnalyticsItemsList } from '../../lib/gtm';

function sentenceCase(str) {
  if (!str) return '';
  const words = str.toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const messages = defineMessages({
  title: { defaultMessage: '{topic} {iaDescription} you might like' },
  seeMore: { defaultMessage: 'See more' }
});

const ActionLink = ({ locale, topic, iaCode }) => {
  return (
    <FormattedMessage {...messages.seeMore}>
      {text => (
        <TextLink
          intent={constants.PRIMARY}
          href={shopTagPath({
            locale,
            tag: topic,
            iaCode,
            ref: TOPIC_PRODUCT_RECOMMENDATIONS_SOURCE
          })}
          iconAfter={<ChevronRightIcon size={constants.MEDIUM} />}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
        </TextLink>
      )}
    </FormattedMessage>
  );
};

const TopicProductCarouselCollection = ({
  logComponentInView,
  addImpression,
  logEvent,
  userInfo,
  profile,
  transparentCards,
  topicLimit,
  urlRef
}) => {
  const { locale, isLoggedIn, country, currency } = userInfo || {};
  const { collection, loading } = useTopicProductRecommendationsCollection({
    country,
    currency,
    locale,
    topicLimit,
    fragments: [...carouselFragments, ...favoriteFragments]
  });

  const showStrikethroughPricing = useStrikethroughFeatureFlag();

  if (loading || !collection) return null;

  return collection.map(
    ({ topic, iaCode, items, iaDescription }, carouselIndex) => {
      const analyticsLabel = `${TOPIC_PRODUCT_RECOMMENDATIONS_LABEL}-${carouselIndex +
        1}`;
      const analyticsSourceLabel = `${TOPIC_PRODUCT_RECOMMENDATIONS_SOURCE}-${carouselIndex +
        1}`;

      if (!Array.isArray(items) || items.length === 0) return null;

      return (
        <Box marginBottom={2}>
          {!!(topic && iaCode && locale) && (
            <FormattedMessage
              {...messages.title}
              values={{
                topic: sentenceCase(topic),
                iaDescription: iaDescription.toLowerCase()
              }}
            >
              {text => (
                <CarouselHeading
                  actionSlot={<ActionLink {...{ locale, topic, iaCode }} />}
                >
                  {text}
                </CarouselHeading>
              )}
            </FormattedMessage>
          )}
          <FavoritableItemCarousel
            profile={profile}
            loading={loading}
            items={items}
            isLoggedIn={isLoggedIn}
            onLoadingShowed={logComponentInView(analyticsSourceLabel, true)}
            onContentShowed={logComponentInView(analyticsSourceLabel, false)}
            logImpression={({ id }, index) => {
              const impression = analyticsPayload.impression(
                analyticsLabel,
                id,
                index + 1
              );
              addImpression(impression);
            }}
            onFavorite={(_, newState, index) => {
              logEvent({
                analytics: analyticsPayload.favorite({
                  newState,
                  rank: index + 1,
                  source: analyticsSourceLabel
                })
              });
            }}
            urlRef={urlRef ? `${urlRef}-${carouselIndex}` : null}
            rows={1}
            transparentCards={transparentCards}
            showStrikethroughPricing={showStrikethroughPricing}
            analyticsComponentID={AnalyticsItemsList.TopicProductCarousel}
            analyticsTopic={topic}
          />
        </Box>
      );
    }
  );
};

TopicProductCarouselCollection.propTypes = {
  transparentCards: PropTypes.bool,
  urlRef: PropTypes.string
};

TopicProductCarouselCollection.defaultProps = {
  transparentCards: true,
  urlRef: TOPIC_PRODUCT_RECOMMENDATIONS_SOURCE
};

export default TopicProductCarouselCollection;
